import React, { useState } from 'react';
import st from './Coaches.module.scss';
import Table from '../../components/Table/Table';
import ModalForm from '../../components/ModalForm/ModalForm';
import { Input, Button, Popconfirm, notification, DatePicker } from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    ExportOutlined,
} from '@ant-design/icons';
import api from '../../config/axiosConfig';
import { exportToExcel, FilterSearch } from '../../utils/utils';

const Coaches = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [coachIsUpdated, setCoachIsUpdated] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentCoach, setCurrentCoach] = useState(null);
    const [tableData, setTableData] = useState([]);

    const handleDataLoaded = (data) => {
        setTableData(data);
    };

    const handleAddEditCoach = async (values) => {
        setLoading(true);
        try {
            const formattedValues = {
                ...values,
                birthDate: values.birthDate
                    ? values.birthDate.format('YYYY-MM-DD')
                    : null,
            };

            if (currentCoach) {
                // Edit Coach
                await api.put(`/coaches/${currentCoach.id}`, formattedValues);
                notification.success({
                    message: 'Success',
                    description: 'Coach updated successfully',
                    placement: 'topRight',
                });
            } else {
                // Add Coach
                await api.post('/coaches/create', formattedValues);
                notification.success({
                    message: 'Success',
                    description: 'Coach added successfully',
                    placement: 'topRight',
                });
            }

            setIsModalOpen(false);
            setCoachIsUpdated((prev) => !prev);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to save coach. Please try again.',
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCoach = async (id) => {
        setLoading(true);
        try {
            await api.delete(`/coaches/${id}`);
            notification.success({
                message: 'Success',
                description: 'Coach has been successfully deleted.',
                placement: 'topRight',
            });
            setCoachIsUpdated((prev) => !prev);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to delete the coach. Please try again.',
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!tableData.length) {
            notification.error({
                message: 'Error',
                description: 'No data available to export.',
                placement: 'topRight',
            });
            return;
        }

        exportToExcel({
            data: tableData,
            columns,
            fileName: 'Coaches_Data',
        });

        notification.success({
            message: 'Success',
            description: 'Coaches data exported successfully.',
            placement: 'topRight',
        });
    };

    const openEditModal = (coach) => {
        setCurrentCoach(coach);
        setEditMode(true);
        setIsModalOpen(true);
    };

    const columns = [
        {
            title: 'სახელი',
            dataIndex: 'firstName',
            sorter: (a, b) =>
                (a.firstName || '').localeCompare(b.firstName || ''),
            filterDropdown: FilterSearch,
            onFilter: (value, record) =>
                (record.firstName || '')
                    .toLowerCase()
                    .includes(value.toLowerCase()),
        },
        {
            title: 'გვარი',
            dataIndex: 'lastName',
            sorter: (a, b) =>
                (a.lastName || '').localeCompare(b.lastName || ''),
            filterDropdown: FilterSearch,
            onFilter: (value, record) =>
                (record.lastName || '').toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'დაბ. თარიღი',
            dataIndex: 'birthDate',
            sorter: (a, b) =>
                new Date(a.birthDate || 0) - new Date(b.birthDate || 0),
            filterDropdown: FilterSearch,
            onFilter: (value, record) =>
                (record.birthDate || '').toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'მობ. ნომერი',
            dataIndex: 'mobileNumber',
            filterDropdown: FilterSearch,
            onFilter: (value, record) =>
                (record.mobileNumber || '')
                    .toLowerCase()
                    .includes(value.toLowerCase()),
        },
        {
            title: 'პ/ნ',
            dataIndex: 'personalNumber',
            sorter: (a, b) =>
                (a.personalNumber || '').localeCompare(b.personalNumber || ''),
            filterDropdown: FilterSearch,
            onFilter: (value, record) =>
                (record.personalNumber || '')
                    .toLowerCase()
                    .includes(value.toLowerCase()),
        },
        {
            title: 'მისამართი',
            dataIndex: 'address',
            filterDropdown: FilterSearch,
            onFilter: (value, record) =>
                (record.address || '').toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'გამოცდილება',
            dataIndex: 'experience',
            filterDropdown: FilterSearch,
            onFilter: (value, record) =>
                (record.experience || '')
                    .toLowerCase()
                    .includes(value.toLowerCase()),
        },
        {
            title: 'კომენტარი',
            dataIndex: 'note',
            filterDropdown: FilterSearch,
            onFilter: (value, record) =>
                (record.note || '').toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <>
                    <Button type="link" onClick={() => openEditModal(record)}>
                        <EditOutlined />
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this coach?"
                        onConfirm={() => handleDeleteCoach(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger>
                            <DeleteOutlined />
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];

    const formItems = [
        [
            {
                label: 'სახელი',
                name: 'firstName',
                rules: [{ required: true }],
                component: <Input />,
                span: 12,
            },
            {
                label: 'გვარი',
                name: 'lastName',
                rules: [{ required: true }],
                component: <Input />,
                span: 12,
            },
        ],
        [
            {
                label: 'დაბ. თარიღი',
                name: 'birthDate',
                rules: [{ required: true }],
                component: <DatePicker />,
                span: 8,
            },
            {
                label: 'მობ. ნომერი',
                name: 'mobileNumber',
                rules: [{ required: true }],
                component: <Input />,
                span: 8,
            },
            {
                label: 'პ/ნ',
                name: 'personalNumber',
                rules: [{ required: true }],
                component: <Input />,
                span: 8,
            },
        ],
        [
            {
                label: 'მისამართი',
                name: 'address',
                component: <Input />,
                span: 24,
            },
        ],
        [
            {
                label: 'გამოცდილება',
                name: 'experience',
                component: <Input />,
                span: 24,
            },
        ],
        [
            {
                label: 'კომენტარი',
                name: 'note',
                component: <Input.TextArea />,
                span: 24,
            },
        ],
    ];

    return (
        <>
            <div className={st.btnContainer}>
                <Button
                    type="primary"
                    onClick={() => {
                        setIsModalOpen(true);
                        setEditMode(false);
                        setCurrentCoach(null);
                    }}
                    style={{ marginBottom: 16 }}
                >
                    Add Coach
                </Button>
                <Button
                    type="default"
                    icon={<ExportOutlined />}
                    onClick={handleExport}
                    style={{ marginBottom: 16, marginLeft: 8 }}
                >
                    Export to Excel
                </Button>
            </div>

            <ModalForm
                open={isModalOpen}
                title={editMode ? 'Edit Coach' : 'Add New Coach'}
                onCancel={() => setIsModalOpen(false)}
                onSubmit={handleAddEditCoach}
                confirmLoading={loading}
                formItems={formItems}
                initialValues={currentCoach}
                rowIsUpdated={coachIsUpdated}
            />

            <Table
                columns={columns}
                url="/coaches"                 // ← relative path only
                rowIsUpdated={coachIsUpdated}
                onDataLoaded={handleDataLoaded}
            />
        </>
    );
};

export default Coaches;
